import React, { useMemo } from 'react';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useToast } from '@/hooks/use-toast';
import { Guest, Mesa } from '@/types';
import { eventService } from '@/services/eventService';
import { MesaCard } from './MesaCard';
import { computeSeatsUsed } from './mesaUtils';
import { UnassignedGuestsPanel } from './UnassignedGuestsPanel';

interface MesasAsignacionesProps {
  mesas: Mesa[];
  guests: Guest[];
  onGuestsChange: (guests: Guest[]) => void;
  canWrite?: boolean;
}

interface DroppableMesaCardProps {
  mesa: Mesa;
  assignedGuests: Guest[];
  onUnassignGuest?: (guestId: string) => void;
}

const DroppableMesaCard: React.FC<DroppableMesaCardProps> = ({
  mesa,
  assignedGuests,
  onUnassignGuest,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `mesa:${mesa.id}`,
    data: { mesaId: mesa.id },
  });
  return (
    <div ref={setNodeRef}>
      <MesaCard
        mesa={mesa}
        assignedGuests={assignedGuests}
        onUnassignGuest={onUnassignGuest}
        isDragOver={isOver}
        variant="asignaciones"
      />
    </div>
  );
};

export const MesasAsignaciones: React.FC<MesasAsignacionesProps> = ({
  mesas,
  guests,
  onGuestsChange,
  canWrite = true,
}) => {
  const { toast } = useToast();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const guestsByMesa = useMemo(() => {
    const map = new Map<string, Guest[]>();
    for (const g of guests) {
      if (g.mesa_id) {
        if (!map.has(g.mesa_id)) map.set(g.mesa_id, []);
        map.get(g.mesa_id)!.push(g);
      }
    }
    return map;
  }, [guests]);

  const unassigned = useMemo(() => guests.filter((g) => !g.mesa_id), [guests]);

  const persistAssignment = async (guestId: string, mesaId: string | null) => {
    const prev = guests;
    const next = guests.map((g) => (g.id === guestId ? { ...g, mesa_id: mesaId } : g));
    onGuestsChange(next);
    try {
      await eventService.assignGuestToMesa(guestId, mesaId);
    } catch (err) {
      console.error('Error assigning guest:', err);
      onGuestsChange(prev);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'No se pudo asignar la mesa',
        variant: 'destructive',
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!canWrite) return;
    const { active, over } = event;
    if (!over) return;
    const activeData = active.data.current as { guestId?: string } | undefined;
    const overData = over.data.current as { mesaId?: string | null } | undefined;
    if (!activeData?.guestId || overData === undefined) return;

    const guest = guests.find((g) => g.id === activeData.guestId);
    if (!guest) return;
    const targetMesaId = overData.mesaId ?? null;
    if (guest.mesa_id === targetMesaId) return;

    if (targetMesaId) {
      const targetMesa = mesas.find((m) => m.id === targetMesaId);
      const occupants = guestsByMesa.get(targetMesaId) ?? [];
      const seatsAfter = computeSeatsUsed(occupants) + 1 + guest.numero_acompanantes;
      if (targetMesa && seatsAfter > targetMesa.capacidad) {
        toast({
          title: 'Mesa con sobrecupo',
          description: `Mesa ${targetMesa.numero} excederá su capacidad (${seatsAfter}/${targetMesa.capacidad}).`,
        });
      }
    }

    void persistAssignment(guest.id, targetMesaId);
  };

  const handleUnassign = (guestId: string) => {
    void persistAssignment(guestId, null);
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          {mesas.length === 0 ? (
            <div className="p-8 text-center text-gray-500 border-2 border-dashed rounded-lg">
              Primero crea las mesas en la pestaña Distribución.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {mesas.map((mesa) => (
                <DroppableMesaCard
                  key={mesa.id}
                  mesa={mesa}
                  assignedGuests={guestsByMesa.get(mesa.id) ?? []}
                  onUnassignGuest={canWrite ? handleUnassign : undefined}
                />
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-1">
          <UnassignedGuestsPanel guests={unassigned} />
        </div>
      </div>
    </DndContext>
  );
};
